import { useQuery } from '@tanstack/react-query';
import api from '@/api/client';
import { API_BASE_URL } from '@/config';
import type { ReportExecutionSummary } from '@/api/reports';

interface ExecutionStatus {
  execution_id: string;
  status: string;
  progress: number;
  stage?: string | null;
  error?: string | null;
  preview_url?: string;
  pdf_url?: string;
}

/**
 * React Query hook for polling execution status.
 * Uses a fixed 1.5s interval while queued/running so completion is detected within ~1.5s.
 */
export const useReportExecution = (executionId: string | null, enabled: boolean = true) => {
  const query = useQuery<ExecutionStatus>({
    queryKey: ['reportExecution', executionId],
    queryFn: async () => {
      if (!executionId) throw new Error('Execution ID required');
      
      const response = await api.get(`${API_BASE_URL}/v1/reports/executions/${executionId}`);
      return {
        execution_id: response.data.execution_id,
        status: response.data.status,
        progress: response.data.progress || 0,
        stage: response.data.stage,
        error: response.data.error,
        preview_url: response.data.preview_url,
        pdf_url: response.data.pdf_url,
      };
    },
    enabled: enabled && !!executionId,
    refetchInterval: (query) => {
      const data = query.state.data;
      if (data?.status === 'completed' || data?.status === 'failed' || data?.status === 'cancelled') {
        return false;
      }
      return 1500; // Poll every 1.5s so "completed" toast appears soon after report is ready
    },
    staleTime: 0,
  });
  
  return query;
};
